import { UserManagerSettings, UserManager } from 'oidc-client-ts'
import { loadOidcSettings } from './oidc-config'

let userManager: UserManager
// Guarding on `userManager` alone would let a caller arriving before
// loadOidcSettings() resolves build a second UserManager.
let userManagerPromise: Promise<UserManager | null> | null = null

function isTopWindow() {
  try {
    return window.self === window.top
  } catch {
    return false
  }
}

/**
 * Silent renew and querySessionStatus deliver their response to `redirect_uri`
 * inside a hidden iframe. Booting the app there leaves the opener unnotified,
 * so its request times out and the session monitor reports the user as signed
 * out; the response only has to be relayed back. Returns true when this
 * document is that iframe, meaning the caller must not bootstrap the app.
 */
export async function completeOidcIframeCallback(): Promise<boolean> {
  const params = new URLSearchParams(window.location.search)
  if (
    isTopWindow() ||
    !params.has('state') ||
    !(params.has('code') || params.has('error'))
  ) {
    return false
  }
  try {
    const oidcSettings: UserManagerSettings = await loadOidcSettings()
    await new UserManager(oidcSettings).signinSilentCallback()
  } catch (e) {
    console.error('Error completing OIDC iframe callback:', e)
  }
  return true
}

export function configureOidc(): Promise<UserManager | null> {
  if (!userManagerPromise) {
    userManagerPromise = createUserManager().catch((e) => {
      console.error('Error configuring OIDC:', e)
      userManagerPromise = null
      return null
    })
  }
  return userManagerPromise
}

async function createUserManager(): Promise<UserManager> {
  const oidcSettings: UserManagerSettings = await loadOidcSettings()
  const mgr = new UserManager(oidcSettings)

  mgr.events.addSilentRenewError((error: Error & { error?: string }) => {
    if (error.error === 'invalid_grant' || error.error === 'invalid_token') {
      clearOidcStorage().then(() => {
        window.location.href = `${window.location.origin}/logout`
      })
    }
  })

  mgr.events.addUserSignedOut(async () => {
    // Only the top window owns the session; a framed instance must not end it.
    if (!isTopWindow()) return
    const idToken = (await mgr.getUser().catch(() => null))?.id_token
    try {
      await clearOidcStorage()
    } catch (error) {
      console.error('Error clearing OIDC storage:', error)
    }
    await mgr.signoutRedirect(idToken ? { id_token_hint: idToken } : {})
  })

  userManager = mgr
  return mgr
}

export async function clearOidcStorage() {
  for (const key in window.localStorage) {
    if (key.startsWith('oidc')) {
      window.localStorage.removeItem(key)
    }
  }
  for (const key in window.sessionStorage) {
    if (key.startsWith('oidc')) {
      window.sessionStorage.removeItem(key)
    }
  }
  if (!userManager) {
    return
  }
  await userManager.clearStaleState()
  await userManager.removeUser()
}

export class OidcAuthService {
  private async getMgr(): Promise<UserManager> {
    if (!userManager) {
      await configureOidc()
    }
    if (!userManager) {
      throw new Error('OIDC manager is not configured')
    }
    return userManager
  }

  async getCurrentUser() {
    try {
      const mgr = await this.getMgr()
      return await mgr.getUser()
    } catch (error) {
      console.error('getCurrentUser failed:', error)
      return null
    }
  }
  async isAuthenticated() {
    const user = await this.getCurrentUser()
    return !!(user && !user.expired)
  }

  async loginWithKeycloak(loginHint?: string, state?: Record<string, unknown>) {
    try {
      const signinParams: Record<string, unknown> = { state }
      if (loginHint) {
        signinParams.login_hint = loginHint
      }
      const mgr = await this.getMgr()
      await mgr.signinRedirect(signinParams)
    } catch (error) {
      console.error('Keycloak login failed:', error)
      throw error
    }
  }
  async handleLoginCallback() {
    try {
      const mgr = await this.getMgr()
      const user = await mgr.signinRedirectCallback()
      return user
    } catch (error) {
      console.error('handleLoginCallback failed:', error)
      throw error
    }
  }

  async silentLogin() {
    try {
      const mgr = await this.getMgr()
      return await mgr.signinSilent()
    } catch (error) {
      console.error('silentLogin failed:', error)
      return null
    }
  }
  async logout() {
    try {
      const user = await this.getCurrentUser()
      if (user) {
        const idToken = user.id_token
        await clearOidcStorage()
        const mgr = await this.getMgr()
        await mgr.signoutRedirect({ id_token_hint: idToken })
      }
    } catch (error) {
      console.error('logout failed:', error)
      if (userManager) {
        await userManager.removeUser()
      }
    }
  }

  async getAccessToken() {
    const user = await this.getCurrentUser()
    return user?.access_token
  }

  async getSsoUserInfoCrossOrigin() {
    try {
      let user = await this.getCurrentUser()
      if (!user || user.expired) {
        user = await this.silentLogin()
      }
      return user || null
    } catch (error) {
      console.error('getSsoUserInfoCrossOrigin failed:', error)
      return null
    }
  }

  async quickLogout(paramsUrl?: string, state?: string) {
    const user = await this.getCurrentUser()
    const redirectUri = window.location.origin + (paramsUrl || '')
    const authority = userManager?.settings?.authority || ''
    const encodedState = state ? encodeURIComponent(state) : ''
    if (user && authority && state !== 'oidc-error') {
      const idToken = user.id_token
      const logoutUrl = `${authority}/protocol/openid-connect/logout?post_logout_redirect_uri=${encodeURIComponent(
        redirectUri
      )}&id_token_hint=${idToken}${
        encodedState ? `&state=${encodedState}` : ''
      }`

      window.location.href = logoutUrl
    } else {
      window.location.href = redirectUri
    }
  }

  async removeUser() {
    const mgr = await this.getMgr()
    return mgr.removeUser()
  }
}

export const authService = new OidcAuthService()
